import { useEffect, useMemo } from 'react';
import { useCondition } from '../ExperimentsProvider';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { useExperimentSpeculativeData } from '../../hooks/useExperimentSpeculativeData';
import { TEAME_223_FLAG_ID, TEAME_223_TREATMENT } from './constants';

interface FutureSelfAvatarPayload {
  image?: string;
  has_seen?: boolean;
}

const DEFAULT_FUTURE_SELF_AVATAR_SPECULATIVE_DATA: FutureSelfAvatarPayload = {};
export const TEAME_223_AVATAR_UPDATED_EVENT = 'teame223-avatar-updated';

interface UseTEAME223SpeculativeAvatarDataResult {
  avatarDataUrl: string | null;
  hasSeenAvatar: boolean;
  isFutureSelfAvatarEnabled: boolean;
  isLoadingSpeculativeData: boolean;
  hasHydratedFutureSelfData: boolean;
}

export const useTEAME223SpeculativeAvatarData = (): UseTEAME223SpeculativeAvatarDataResult => {
  const user = useAppSelector(selectUser);
  const variantName = useCondition(TEAME_223_FLAG_ID);
  const isFutureSelfAvatarEnabled = variantName === TEAME_223_TREATMENT;
  const userNodeId = user?.id ? `UserNode:${user.id}` : undefined;

  const {
    data: avatarSpeculativeData,
    isLoading: isLoadingSpeculativeData,
    hydrated: hasHydratedFutureSelfData,
    reload,
  } = useExperimentSpeculativeData<FutureSelfAvatarPayload>({
    publicUserId: user?.sub,
    userNodeId,
    flagName: TEAME_223_FLAG_ID,
    variantName,
    defaultValues: DEFAULT_FUTURE_SELF_AVATAR_SPECULATIVE_DATA,
    enabled: isFutureSelfAvatarEnabled,
  });

  const hasSeenAvatar = Boolean(avatarSpeculativeData.has_seen || avatarSpeculativeData.image);

  const avatarDataUrl = useMemo(() => {
    const avatarImageBase64 = avatarSpeculativeData.image;

    if (!isFutureSelfAvatarEnabled || !hasSeenAvatar || !avatarImageBase64) {
      return null;
    }

    if (avatarImageBase64.startsWith('data:image/')) {
      return avatarImageBase64;
    }

    return `data:image/png;base64,${avatarImageBase64}`;
  }, [avatarSpeculativeData.image, hasSeenAvatar, isFutureSelfAvatarEnabled]);

  useEffect(() => {
    const handleAvatarUpdated = () => {
      reload();
    };

    window.addEventListener(TEAME_223_AVATAR_UPDATED_EVENT, handleAvatarUpdated);

    return () => {
      window.removeEventListener(TEAME_223_AVATAR_UPDATED_EVENT, handleAvatarUpdated);
    };
  }, [reload]);

  return {
    avatarDataUrl,
    hasSeenAvatar,
    isFutureSelfAvatarEnabled,
    isLoadingSpeculativeData,
    hasHydratedFutureSelfData,
  };
};
